import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import { 
  Building2, Search, Filter, Edit3, Trash2, ShieldCheck, Plus, Server, 
  Users, Globe, Activity, CheckCircle, AlertTriangle, RefreshCw, Power, Lock
} from 'lucide-react';
import { showSuccess, showError } from '../../../lib/alerts';

interface Tenant {
  id: string;
  name: string;
  slug?: string;
  domain?: string;
  plan?: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'DELETED' | string;
  usersCount?: number;
  workspacesCount?: number;
  projectsCount?: number;
  invoicesCount?: number;
  createdAt: string;
}

export default function TenantsAdminView() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('ALL');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    plan: 'Pro',
    status: 'ACTIVE',
    ownerEmail: '',
    ownerName: '',
  });

  // Query Tenants
  const { data: tenants = [], isLoading, isError, refetch } = useQuery<Tenant[]>({
    queryKey: ['admin-tenants'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/tenants');
      const payload = res.data?.data ?? res.data;
      return Array.isArray(payload) ? payload : [];
    },
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => apiClient.post('/api/admin/tenants', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      setIsModalOpen(false);
      showSuccess('Tenant criado com sucesso!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao criar tenant.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<typeof formData> }) =>
      apiClient.put(`/api/admin/tenants/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      setIsModalOpen(false);
      showSuccess('Tenant atualizado com sucesso!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao atualizar tenant.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/admin/tenants/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      showSuccess('Tenant excluído com sucesso!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao excluir tenant.');
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'suspend' | 'activate' }) =>
      apiClient.post(`/api/admin/tenants/${id}/${action}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      showSuccess('Status do tenant atualizado.');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao alterar status.');
    },
  });

  const handleOpenCreate = () => {
    setIsCreateMode(true);
    setSelectedTenant(null);
    setFormData({
      name: '',
      slug: '',
      plan: 'Pro',
      status: 'ACTIVE',
      ownerEmail: '',
      ownerName: '',
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (tenant: Tenant) => {
    setIsCreateMode(false);
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name || '',
      slug: tenant.slug || tenant.domain || '',
      plan: tenant.plan || 'Pro',
      status: tenant.status || 'ACTIVE',
      ownerEmail: '',
      ownerName: '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Tem certeza que deseja excluir o tenant "${name}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleStatus = (tenant: Tenant) => {
    const action = tenant.status === 'ACTIVE' ? 'suspend' : 'activate';
    toggleStatusMutation.mutate({ id: tenant.id, action });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    if (isCreateMode) {
      createMutation.mutate(formData);
    } else if (selectedTenant) {
      updateMutation.mutate({ id: selectedTenant.id, data: formData });
    }
  };

  const filteredTenants = tenants.filter((t) => {
    const matchesSearch =
      t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.domain?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = planFilter === 'ALL' || t.plan?.toUpperCase() === planFilter.toUpperCase();
    return matchesSearch && matchesPlan;
  });

  const totalTenantsCount = tenants.length;
  const activeCount = tenants.filter((t) => t.status === 'ACTIVE').length;
  const enterpriseCount = tenants.filter((t) => t.plan?.toUpperCase() === 'ENTERPRISE').length;
  const proCount = tenants.filter((t) => t.plan?.toUpperCase() === 'PRO').length;

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-[1400px] mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-indigo-600 tracking-wider uppercase mb-1">
            <Server size={14} /> SaaS Multi-Tenant Cloud
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Workspaces & SaaS Tenants
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Gerenciamento global de organizações, isolamento de dados e planos corporativos no PostgreSQL.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-medium shadow-xs transition-all"
          >
            <RefreshCw size={13} className={isLoading ? 'animate-spin' : ''} /> Atualizar
          </button>
          <button
            onClick={handleOpenCreate}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-semibold shadow-xs transition-all"
          >
            <Plus size={14} /> Novo Tenant
          </button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Total de Tenants</p>
          <h3 className="text-2xl font-bold text-gray-900 mt-1">{totalTenantsCount}</h3>
          <span className="text-[11px] text-gray-500 mt-1 block">Organizações registradas</span>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Tenants Ativos</p>
          <h3 className="text-2xl font-bold text-emerald-600 mt-1">{activeCount}</h3>
          <span className="text-[11px] text-emerald-600 font-medium mt-1 block">Operando normalmente</span>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Plano Pro</p>
          <h3 className="text-2xl font-bold text-indigo-600 mt-1">{proCount}</h3>
          <span className="text-[11px] text-indigo-600 font-medium mt-1 block">Assinaturas Pro</span>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Enterprise</p>
          <h3 className="text-2xl font-bold text-purple-600 mt-1">{enterpriseCount}</h3>
          <span className="text-[11px] text-purple-600 font-medium mt-1 block">Contratos Dedicados</span>
        </div>
      </div>

      {/* Filter & Search */}
      <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:w-80">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome ou slug..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500 transition-all"
          />
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <Filter size={14} className="text-gray-400" />
          <span className="text-xs font-semibold text-gray-600">Plano:</span>
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-hidden focus:bg-white"
          >
            <option value="ALL">Todos os Planos</option>
            <option value="STARTER">Starter</option>
            <option value="PRO">Pro</option>
            <option value="ENTERPRISE">Enterprise</option>
          </select>
        </div>
      </div>

      {/* Tenants Table */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs text-gray-500 mt-3 font-medium">Carregando tenants do banco PostgreSQL...</p>
          </div>
        ) : isError ? (
          <div className="p-8 text-center">
            <AlertTriangle size={32} className="mx-auto text-rose-500 mb-2" />
            <p className="text-sm font-semibold text-gray-800">Falha ao carregar tenants</p>
            <p className="text-xs text-gray-500 mt-1">Verifique sua conexão ou permissões de administrador.</p>
            <button
              onClick={() => refetch()}
              className="mt-3 px-3 py-1.5 bg-indigo-50 text-indigo-600 text-xs font-medium rounded-lg hover:bg-indigo-100 transition-all"
            >
              Tentar Novamente
            </button>
          </div>
        ) : filteredTenants.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm font-semibold text-gray-700">Nenhum tenant encontrado</p>
            <p className="text-xs text-gray-400 mt-1">
              {searchTerm ? 'Tente ajustar os termos da busca.' : 'Clique em "Novo Tenant" para cadastrar a primeira organização.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50/75 border-b border-gray-200/80 text-gray-500 font-semibold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3 px-4">Organização</th>
                  <th className="py-3 px-4">Plano</th>
                  <th className="py-3 px-4">Workspaces</th>
                  <th className="py-3 px-4">Usuários</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Criado em</th>
                  <th className="py-3 px-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3.5 px-4 font-medium text-gray-900">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-xs">
                          {tenant.name?.charAt(0) || 'T'}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{tenant.name}</p>
                          <span className="text-[10px] text-gray-400">{tenant.slug || tenant.domain || 'sem slug'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                        tenant.plan?.toUpperCase() === 'ENTERPRISE'
                          ? 'bg-purple-50 text-purple-700 border border-purple-200'
                          : tenant.plan?.toUpperCase() === 'PRO'
                          ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {tenant.plan || 'Pro'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-gray-600 font-semibold">{tenant.workspacesCount ?? 1}</td>
                    <td className="py-3.5 px-4 text-gray-600 font-semibold">{tenant.usersCount ?? 1}</td>
                    <td className="py-3.5 px-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                        tenant.status === 'ACTIVE'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-rose-50 text-rose-700'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${tenant.status === 'ACTIVE' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                        {tenant.status === 'ACTIVE' ? 'Ativo' : 'Suspenso'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-gray-500 text-[11px]">
                      {new Date(tenant.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => handleToggleStatus(tenant)}
                          title={tenant.status === 'ACTIVE' ? 'Suspender Tenant' : 'Ativar Tenant'}
                          className="p-1.5 text-gray-400 hover:text-amber-600 rounded-lg hover:bg-gray-100 transition-all"
                        >
                          <Power size={13} />
                        </button>
                        <button
                          onClick={() => handleOpenEdit(tenant)}
                          title="Editar Tenant"
                          className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-gray-100 transition-all"
                        >
                          <Edit3 size={13} />
                        </button>
                        <button
                          onClick={() => handleDelete(tenant.id, tenant.name)}
                          title="Excluir Tenant"
                          className="p-1.5 text-gray-400 hover:text-rose-600 rounded-lg hover:bg-gray-100 transition-all"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 animate-in zoom-in-95 duration-200">
            <h2 className="text-lg font-bold text-gray-900 mb-1">
              {isCreateMode ? 'Novo Tenant / Organização' : 'Editar Tenant'}
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Preencha os dados da organização para configuração no Cloud SQL.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Nome da Empresa</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: CyberCorp Soluções"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Slug / Domínio</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="Ex: cybercorp (opcional)"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Plano</label>
                  <select
                    value={formData.plan}
                    onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-hidden"
                  >
                    <option value="Starter">Starter</option>
                    <option value="Pro">Pro</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-hidden"
                  >
                    <option value="ACTIVE">Ativo</option>
                    <option value="SUSPENDED">Suspenso</option>
                  </select>
                </div>
              </div>

              {isCreateMode && (
                <div className="pt-2 border-t border-gray-100">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">E-mail do Administrador (Opcional)</label>
                  <input
                    type="email"
                    value={formData.ownerEmail}
                    onChange={(e) => setFormData({ ...formData, ownerEmail: e.target.value })}
                    placeholder="admin@empresa.com"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500"
                  />
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-all disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Salvando...' : 'Salvar Tenant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
